// Файл index.js

import { storage } from '../utils/storage';
import { session_storage } from '../utils/session_storage';
import { clear_user_storage } from '../utils/user_storage_cleanup';

// Главный контейнер страницы авторизации: 
// хранит state, refs,
// подключает эффекты и передаёт данные в render.js

import { useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from '../utils/simple_router';

import { apiService } from '../services/api.js';
import {
    DEFAULT_INTERFACE_CUSTOMIZATION_SETTINGS,
    install_interface_customization_settings,
    start_section_path,
} from '../shared_code/interface_customization/interface_customization_store';
import {
    load_interface_customization_settings,
} from '../services/access_context_parts/interface_profile_request';
import { getImage } from '../components/DropdownButton/utils/image';
import { useToast } from '../components/Toast';

import './page_authorization.css';
import './page_authorization_landscape.css';
import './page_authorization_mobile.css';

import {
    COOKIE_STORAGE_KEY,
    PD_STORAGE_KEY,
    is_consent_still_valid,
    clear_consent,
    save_cookie_consent_accepted,
} from './consent_storage';

import { use_auth_effects } from './auth_effects';
import { build_going_after_authorization } from './logic';
import { Page_authorization_view } from './render';
import { use_authorization_viewport } from './use_authorization_viewport';



const Page_authorization = () => {
    use_authorization_viewport();

    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    const [loading, set_loading] = useState(false);
    const [var_8, set_var_8] = useState('');
    const [var_10, set_var_10] = useState('');
    const [response, set_response] = useState('');
    const [auth_locked_until, set_auth_locked_until] = useState(null);
    const [auth_lock_remaining, set_auth_lock_remaining] = useState(0);
    const [show_repeat_password, set_show_repeat_password] = useState(false);
    const [show_button_register, set_show_button_register] = useState(true);
    const [is_pd_accepted, set_is_pd_accepted] = useState(false);
    const [is_cookies_accepted, set_is_cookies_accepted] = useState(false);
    const [pd_already_accepted, set_pd_already_accepted] = useState(false);
    const [
        cookies_already_accepted,
        set_cookies_already_accepted,
    ] = useState(false);
    const [is_pd_policy_open, set_is_pd_policy_open] = useState(false);
    const [
        is_cookies_policy_open,
        set_is_cookies_policy_open,
    ] = useState(false);

    const phone_ref = useRef(null);
    const password_ref = useRef(null);
    const auth_check_started_ref = useRef(false);

    const image_house = getImage({
        file_name: 'Жилой_дом_3.png',
        version: '20260730-png',
    });

    const redirect_after_login = useCallback(async () => {
        const fromPath = location.state?.from?.pathname;
        const lastVisited = storage.get_item(
            'lastVisitedPage'
        );

        try {
            await apiService.refresh_csrf_token();

            const interface_settings = await (
                load_interface_customization_settings(apiService)
            ).catch(() => install_interface_customization_settings(
                DEFAULT_INTERFACE_CUSTOMIZATION_SETTINGS
            ));
            const configured_start_path = start_section_path(
                interface_settings
            );
            const rightsRes = await apiService.df({
                table_name: 'get_df_template_reference',
                params: null,
            });
            const rights_data = rightsRes?.data || [];

            try {
                session_storage.set_item(
                    'potale_access_rights',
                    JSON.stringify({
                        saved_at: Date.now(),
                        data: rights_data,
                    })
                );
                session_storage.set_item(
                    'potale_auth_verified_at',
                    String(Date.now())
                );

            } catch (error) {
                // sessionStorage недоступен или переполнен.
            }

            const hasLK = rights_data.some(
                (row) =>
                    row['Код 1'] === '1'
                    || row['Код 2'] === '1'
                    || row['Код 1']?.includes('1.')
                    || row['Код 2']?.startsWith('1.')
            );
            const hasBux = rights_data.some(
                (row) =>
                    row['Код 1'] === '2'
                    || row['Код 2'] === '2'
                    || row['Код 1']?.includes('2.')
                    || row['Код 2']?.startsWith('2.')
            );
            const hasDeveloper = rights_data.some(
                (row) =>
                    row['Код 1'] === '5'
                    || row['Код 2'] === '5'
                    || row['Код 1']?.includes('5.')
                    || row['Код 2']?.startsWith('5.')
            );
            const hasOffice = rights_data.some(
                (row) =>
                    row['Код 1'] === '6'
                    || row['Код 2'] === '6'
                    || row['Код 1']?.includes('6.')
                    || row['Код 2']?.startsWith('6.')
            );

            const requested_path = [
                fromPath,
                configured_start_path,
                interface_settings.start_section === 'last'
                    ? lastVisited
                    : null,
            ].find((path_value) => (
                ['/LK', '/bux', '/developer', '/office'].includes(path_value)
            ));
            const requested_allowed = (
                (requested_path === '/LK' && hasLK)
                || (requested_path === '/bux' && hasBux)
                || (requested_path === '/developer' && hasDeveloper)
                || (requested_path === '/office' && hasOffice)
            );

            if (requested_allowed) {
                storage.set_item(
                    'lastVisitedPage',
                    requested_path
                );
                navigate(requested_path, { replace: true });

            } else if (hasOffice) {
                navigate('/office', { replace: true });

            } else if (hasDeveloper) {
                navigate('/developer', { replace: true });

            } else if (hasBux) {
                navigate('/bux', { replace: true });

            } else if (hasLK) {
                navigate('/LK', { replace: true });

            } else {
                showToast('Нет доступа к разделам', 'error');
                await apiService.df({ table_name: 'exit' });
                clear_user_storage();
                window.location.replace('/');
            }

        } catch (err) {
            showToast(`Ошибка проверки прав${err}`, 'error');
        }
    }, [apiService, location.state, navigate, showToast]);

    const going_after_authorization = build_going_after_authorization({
        var_8,
        var_10,
        is_pd_accepted,
        auth_lock_remaining,
        is_cookies_accepted,
        phone_ref,
        password_ref,
        set_loading,
        set_response,
        set_auth_locked_until,
        showToast,
        apiService,
        redirect_after_login,
        save_cookie_consent_accepted,
    });

    use_auth_effects({
        auth_locked_until,
        set_auth_lock_remaining,
        set_auth_locked_until,
        is_consent_still_valid,
        PD_STORAGE_KEY,
        COOKIE_STORAGE_KEY,
        set_pd_already_accepted,
        set_is_pd_accepted,
        set_cookies_already_accepted,
        set_is_cookies_accepted,
        apiService,
        redirect_after_login,
        auth_check_started_ref,
    });

    const show_field_repeat_password = () => {
        set_show_button_register(false);
        set_show_repeat_password(true);
    };

    const page_load_mobile_version = (e) => {
        e.preventDefault();
        window.location.href = '/page_load_mobile_version';
    };

    const revoke_pd_consent = () => {
        clear_consent(PD_STORAGE_KEY);
        set_pd_already_accepted(false);
        set_is_pd_accepted(false);
    };

    const revoke_cookies_consent = () => {
        clear_consent(COOKIE_STORAGE_KEY);
        set_cookies_already_accepted(false);
        set_is_cookies_accepted(false);
    };

    return (
        <Page_authorization_view
            loading={loading}
            response={response}
            var_8={var_8}
            set_var_8={set_var_8}
            var_10={var_10}
            set_var_10={set_var_10}
            auth_lock_remaining={auth_lock_remaining}
            show_repeat_password={show_repeat_password}
            show_button_register={show_button_register}
            is_pd_accepted={is_pd_accepted}
            set_is_pd_accepted={set_is_pd_accepted}
            is_cookies_accepted={is_cookies_accepted}
            set_is_cookies_accepted={set_is_cookies_accepted}
            pd_already_accepted={pd_already_accepted}
            cookies_already_accepted={cookies_already_accepted}
            is_pd_policy_open={is_pd_policy_open}
            set_is_pd_policy_open={set_is_pd_policy_open}
            is_cookies_policy_open={is_cookies_policy_open}
            set_is_cookies_policy_open={set_is_cookies_policy_open}
            phone_ref={phone_ref}
            password_ref={password_ref}
            image_house={image_house}
            show_field_repeat_password={show_field_repeat_password}
            page_load_mobile_version={page_load_mobile_version}
            revoke_pd_consent={revoke_pd_consent}
            revoke_cookies_consent={revoke_cookies_consent}
            going_after_authorization={going_after_authorization}
        />
    );
};


export default Page_authorization;
